import { z } from "zod";
import { validationIssueSchema, type ValidationIssue } from "./message.js";

export enum AnalysisScope {
  PACKET = "PACKET",
  HEADER = "HEADER",
  FIELD = "FIELD",
  ROLE = "ROLE",
  SEQUENCE = "SEQUENCE",
  TIMING = "TIMING",
  CHECKSUM = "CHECKSUM",
  DEFINITION = "DEFINITION",
  ENCODING = "ENCODING",
}

export const analysisScopeSchema = z.nativeEnum(AnalysisScope);

export interface AnalysisIssue extends ValidationIssue {
  scope?: AnalysisScope;
  expected?: string;
  actual?: string;
  ruleSource?: "OFFICIAL" | "PUBLIC_REFERENCE" | "POC" | "UNKNOWN";
}

export const analysisIssueSchema = validationIssueSchema.extend({
  scope: analysisScopeSchema.optional(),
  expected: z.string().optional(),
  actual: z.string().optional(),
  ruleSource: z
    .enum(["OFFICIAL", "PUBLIC_REFERENCE", "POC", "UNKNOWN"])
    .optional(),
});

export interface MessageAnalysisResult {
  id: string;
  messageEventId: string;
  projectId: string;
  environment: string;
  messageType: number;
  messageName: string;
  overallStatus: string;
  definitionStatus: string;
  definitionResults: AnalysisIssue[];
  headerResults: AnalysisIssue[];
  fieldResults: AnalysisIssue[];
  roleResults: AnalysisIssue[];
  sequenceResults: AnalysisIssue[];
  timingResults: AnalysisIssue[];
  summary: {
    pass: number;
    warn: number;
    fail: number;
    unknown: number;
    unsupported: number;
  };
  analyzedAt: string;
  analyzerVersion: string;
  dictionaryVersion?: string;
  metadata?: Record<string, unknown>;
}

export const messageAnalysisResultSchema = z.object({
  id: z.string(),
  messageEventId: z.string(),
  projectId: z.string(),
  environment: z.string(),
  messageType: z.number().int(),
  messageName: z.string(),
  overallStatus: z.string(),
  definitionStatus: z.string(),
  definitionResults: z.array(analysisIssueSchema),
  headerResults: z.array(analysisIssueSchema),
  fieldResults: z.array(analysisIssueSchema),
  roleResults: z.array(analysisIssueSchema),
  sequenceResults: z.array(analysisIssueSchema),
  timingResults: z.array(analysisIssueSchema),
  summary: z.object({
    pass: z.number().int(),
    warn: z.number().int(),
    fail: z.number().int(),
    unknown: z.number().int(),
    unsupported: z.number().int(),
  }),
  analyzedAt: z.string().datetime(),
  analyzerVersion: z.string(),
  dictionaryVersion: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
});
